/**
 * useBlobUploader hook — manages background S3 uploads with per-item progress tracking.
 * Used by the Create page to upload audio recordings and photos immediately when captured,
 * rather than waiting for the Submit All button.
 */

import { useState, useCallback, useRef } from 'react';
import { addApi } from '@/services/addApi';
import { createLogger } from '@/lib/logger';

const log = createLogger('useBlobUploader');

export type UploadStatus = 'uploading' | 'uploaded' | 'failed';

export interface BlobUploadEntry {
  id: string;
  s3Key: string;
  uploadId: string;
  progress: number;
  status: UploadStatus;
}

export function useBlobUploader() {
  const [uploads, setUploads] = useState<Map<string, BlobUploadEntry>>(new Map());
  const activeUploadsRef = useRef<Set<string>>(new Set());

  const uploadBlob = useCallback(async (
    id: string,
    blob: Blob,
    filename: string,
    contentType: string,
  ): Promise<BlobUploadEntry | null> => {
    // Prevent duplicate uploads for the same id
    if (activeUploadsRef.current.has(id)) {
      log.debug('Upload already in progress', { id });
      return null;
    }
    activeUploadsRef.current.add(id);

    // Set initial uploading state
    setUploads(prev => {
      const next = new Map(prev);
      next.set(id, { id, s3Key: '', uploadId: '', progress: 0, status: 'uploading' });
      return next;
    });

    try {
      // Get presigned URL
      const response = await addApi.getUploadUrl(filename, contentType);

      // Upload to S3 with progress tracking
      await addApi.uploadMedia(response.presignedUrl, blob, contentType, (progress) => {
        setUploads(prev => {
          const next = new Map(prev);
          const entry = next.get(id);
          if (entry) {
            next.set(id, { ...entry, progress });
          }
          return next;
        });
      });

      // Mark as uploaded
      const completedEntry: BlobUploadEntry = {
        id,
        s3Key: response.s3Key,
        uploadId: response.uploadId,
        progress: 100,
        status: 'uploaded',
      };
      setUploads(prev => {
        const next = new Map(prev);
        next.set(id, completedEntry);
        return next;
      });

      log.info('Blob uploaded', { id, s3Key: response.s3Key });
      activeUploadsRef.current.delete(id);
      return completedEntry;
    } catch (err) {
      log.error('Blob upload failed', err as Error, { id });
      setUploads(prev => {
        const next = new Map(prev);
        const entry = next.get(id);
        if (entry) {
          next.set(id, { ...entry, status: 'failed' });
        }
        return next;
      });
      activeUploadsRef.current.delete(id);
      return null;
    }
  }, []);

  const removeUpload = useCallback((id: string) => {
    activeUploadsRef.current.delete(id);
    setUploads(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  const clearUploads = useCallback(() => {
    activeUploadsRef.current.clear();
    setUploads(new Map());
  }, []);

  // Derived states
  const uploadsArray = Array.from(uploads.values());
  const anyUploading = uploadsArray.some(u => u.status === 'uploading');
  const anyFailed = uploadsArray.some(u => u.status === 'failed');
  const allUploaded = uploadsArray.length > 0 && uploadsArray.every(u => u.status === 'uploaded');

  return {
    uploads,
    uploadBlob,
    removeUpload,
    clearUploads,
    allUploaded,
    anyUploading,
    anyFailed,
  };
}
